/**
 * ProviderQueuesPage — same shape as admin QueuesPage but scoped server-side
 * to the calling provider. Counters update in realtime via WS hints.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtime } from '@/contexts/RealtimeContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Inbox, RefreshCw, ArrowRight, Archive } from 'lucide-react';
import { fetchQueues, fetchQueueItems, fetchStateMachines } from '@/services/workflowApi';
import { fetchWithFallback } from '@/services/resilientCache';
import StateBadge from '@/components/workflow/StateBadge';
import OperationDetailDrawer from '@/components/workflow/OperationDetailDrawer';
import { queueLabelEs, opTypeLabelEs } from '@/components/workflow/labels';

const ProviderQueuesPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const { subscribe } = useRealtime();
  const [queues, setQueues] = useState({});
  const [counts, setCounts] = useState({});
  const [stateMachines, setStateMachines] = useState(null);
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState([]);
  const [drawerId, setDrawerId] = useState(null);
  const [staleAge, setStaleAge] = useState(0);

  const loadMeta = useCallback(async () => {
    const uid = user?.id;
    const [q, sm] = await Promise.all([
      fetchWithFallback('provider:queues', uid, () => fetchQueues(getAuthHeaders)),
      fetchWithFallback('provider:state-machines', uid, () => fetchStateMachines(getAuthHeaders)),
    ]);
    setQueues(q.data?.queues || {});
    setCounts(q.data?.counts || {});
    setStateMachines(sm.data);
    const ages = [q, sm].filter((x) => x.fromCache).map((x) => x.age);
    setStaleAge(ages.length ? Math.max(...ages) : 0);
    const names = Object.keys(q.data?.queues || {});
    if (names.length && !selected) setSelected(names[0]);
  }, [getAuthHeaders, selected, user?.id]);

  const loadItems = useCallback(async () => {
    if (!selected || !user?.id) return;
    const r = await fetchWithFallback(`provider:queue:${selected}`, user.id, () => fetchQueueItems(getAuthHeaders, selected));
    setItems(r.data?.items || []);
  }, [getAuthHeaders, selected, user?.id]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => {
    const offs = [
      subscribe('operation.assigned', () => { loadMeta(); loadItems(); }),
      subscribe('operation.transition', () => { loadMeta(); loadItems(); }),
    ];
    return () => offs.forEach((f) => f && f());
  }, [subscribe, loadMeta, loadItems]);

  useEffect(() => {
    const onOnline = () => { loadMeta(); loadItems(); };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [loadMeta, loadItems]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Inbox className="w-6 h-6 text-blue-600" />
            Cola Operacional
          </h1>
          <p className="text-xs text-slate-500">Filtrada automáticamente por tu rol</p>
        </div>
        <div className="flex items-center gap-2">
          {staleAge > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 ring-1 ring-amber-300 px-2 py-0.5 rounded-full" data-testid="queues-cache-badge">
              <Archive className="w-3 h-3" />cache · {Math.round(staleAge / 1000)}s
            </span>
          )}
          <Button size="sm" variant="outline" onClick={() => { loadMeta(); loadItems(); }} data-testid="provider-queues-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" />Refrescar</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {Object.entries(queues).map(([name, spec]) => (
          <button
            key={name}
            onClick={() => setSelected(name)}
            data-testid={`provider-queue-card-${name}`}
            className={`text-left p-4 rounded-lg border-2 transition-all ${selected === name ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-300'}`}
          >
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{opTypeLabelEs(spec.op_type)}</p>
            <p className="text-sm font-bold text-slate-900 mt-0.5">{queueLabelEs(name)}</p>
            <div className="flex items-center justify-between mt-3">
              <span className="text-2xl font-bold text-blue-600">{counts[name] ?? 0}</span>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </div>
          </button>
        ))}
      </div>

      {selected && (
        <Card className="overflow-hidden">
          <div className="p-3 border-b bg-slate-50">
            <p className="text-sm font-semibold text-slate-900">{queueLabelEs(selected)} · {items.length}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="p-3 text-left">ID</th>
                  <th className="p-3 text-left">Estado</th>
                  <th className="p-3 text-left">Ruta</th>
                  <th className="p-3 text-left">Actualizado</th>
                  <th className="p-3 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? <tr><td colSpan="5" className="p-6 text-center text-slate-400 italic">Cola vacía</td></tr>
                : items.map((o) => (
                  <tr key={o.id} className="border-t hover:bg-slate-50" data-testid={`provider-queue-item-${o.id}`}>
                    <td className="p-3 font-mono text-[10px]">{o.id.slice(0, 12)}…</td>
                    <td className="p-3"><StateBadge state={o.state} /></td>
                    <td className="p-3">{o.route_origin && o.route_destination ? `${o.route_origin} → ${o.route_destination}` : '—'}</td>
                    <td className="p-3 text-slate-500">{new Date(o.updated_at).toLocaleString()}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => setDrawerId(o.id)} data-testid={`provider-queue-open-${o.id}`}>Abrir</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {drawerId && (
        <OperationDetailDrawer
          operationId={drawerId}
          stateMachines={stateMachines}
          onClose={() => setDrawerId(null)}
          onChanged={loadItems}
        />
      )}
    </div>
  );
};

export default ProviderQueuesPage;
